import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MdCallMerge } from 'react-icons/md';
import { FaEdit, FaTrashAlt, FaHistory, FaArchive } from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import EditCommanderModal from '../modals/EditCommanderModal';
import AddCommander from '../modals/AddCommanderModal';
import MergeCommanderModal from '../modals/MergeCommanderModal';
import CommanderHistoryView from '../components/CommanderHistoryView';
import IngestionReviewView from '../components/IngestionReviewView';
import CommanderOrphanAdminView from '../components/CommanderOrphanAdminView';
import HasPermission from '../utils/HasPermission';
import { AuthContext } from '../context/AuthContext';
import './css/Commanders.css';
import { AdminTabs, AppButton, DataTableShell, FilterRow, PageShell } from "../components/ui";

Modal.setAppElement('#root');

const apiUrl = API_URL;

const statusFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

const cityTypeFilterOptions = [
  { value: 'primary', label: 'Main' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'farm', label: 'Farm' },
];

const rankFilterOptions = [
  { value: 'N/A', label: 'N/A' },
  { value: 'R1', label: 'R1' },
  { value: 'R2', label: 'R2' },
  { value: 'R3', label: 'R3' },
  { value: 'R4', label: 'R4' },
  { value: 'R5', label: 'R5' },
];

const mobileRankingOptions = [
  { value: 'battlePower', label: 'Battle Power', getValue: (commander) => commander.battlePower },
  { value: 'individualReputation', label: 'Reputation', getValue: (commander) => commander.rankingData?.individualReputation },
  { value: 'militaryRank', label: 'Military Rank', getValue: (commander) => commander.militaryRank },
  { value: 'totalMerit', label: 'Total Merit', getValue: (commander) => commander.totalMerit },
  { value: 'kills', label: 'Kills', getValue: (commander) => commander.rankingData?.kills },
  { value: 'losses', label: 'Losses', getValue: (commander) => commander.rankingData?.losses },
  { value: 'officerPower', label: 'Officer Power', getValue: (commander) => commander.rankingData?.officer?.power ?? commander.officerPower },
  { value: 'titanPower', label: 'Titan Power', getValue: (commander) => commander.rankingData?.titan?.power ?? commander.titanPower },
  { value: 'warplanePower', label: 'Warplane Power', getValue: (commander) => commander.rankingData?.warplane?.power ?? commander.warplanePower },
  { value: 'islandScore', label: 'Island', getValue: (commander) => commander.rankingData?.island?.score ?? commander.islandScore },
  { value: 'antiquesScore', label: 'Antiques', getValue: (commander) => commander.rankingData?.antiques?.score ?? commander.antiquesScore },
];

const formatRankingValue = (value) => {
  if (value === undefined || value === null || value === '') return 'N/A';
  if (typeof value === 'number') return value.toLocaleString();
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && String(value).trim() !== '') return numericValue.toLocaleString();
  return String(value);
};

const descendingDefaultSortKeys = new Set([
  'battlePower',
  'individualReputation',
  'totalMerit',
  'kills',
  'losses',
  'officerPower',
  'titanPower',
  'warplanePower',
  'islandScore',
  'antiquesScore',
]);

const Commanders = () => {
  const { user } = useContext(AuthContext);
  const axios = axiosInstance;
  const location = useLocation();
  const navigate = useNavigate();

  const isHistoryView = location.pathname.endsWith('/history');
  const isReviewIngestionView = location.pathname.endsWith('/review-ingestion');
  const isOrphansView = location.pathname.endsWith('/orphans');
  const isCommanderListView = !isHistoryView && !isReviewIngestionView && !isOrphansView;
  const canWriteCommanders = HasPermission('writeCommanders');
  const canDeleteCommanders = HasPermission('deleteCommanders');
  const canReadHistory = HasPermission('readCommandersHistory');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [commanders, setCommanders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 100;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCommander, setSelectedCommander] = useState(null);
  const [isAddCommanderModalOpen, setIsAddCommanderModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    nation: '',
    alliance: '',
    name: '',
    ranks: [],
    statuses: ['active'],
    cityRoles: ['primary', 'secondary'],
    battlePower: '',
    officerPower: '',
    titanPower: '',
    warplanePower: '',
    cityLevelMin: 1,
    cityLevelMax: 41,
  });
  const [sortConfig, setSortConfig] = useState({ key: 'battlePower', direction: 'desc' });

  const [selectedCommanderIds, setSelectedCommanderIds] = useState([]);
  const [selectedCommanderMap, setSelectedCommanderMap] = useState({});
  const [bulkAction, setBulkAction] = useState('viewHistory');
  const [isLoadingCommanders, setIsLoadingCommanders] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [mobileRankingKey, setMobileRankingKey] = useState('battlePower');
  const commanderFetchAbortControllerRef = useRef(null);
  const commanderFetchSequenceRef = useRef(0);

  const getNationId = useCallback((nationValue) => {
    if (!nationValue) return '';
    if (typeof nationValue === 'object') {
      return String(nationValue._id || nationValue.id || '');
    }
    return String(nationValue);
  }, []);

  const getNationNumber = useCallback((nationValue) => {
    if (!nationValue) return '';
    if (typeof nationValue === 'object') {
      const rawNumber = nationValue.number;
      return rawNumber === undefined || rawNumber === null ? '' : String(rawNumber);
    }
    return '';
  }, []);

  const userNationId = getNationId(user?.nation);
  const userNationNumber = getNationNumber(user?.nation);

  const fetchCommanders = useCallback(async (page = 1, sortKey = sortConfig.key, sortDirection = sortConfig.direction) => {
    const requestSequence = commanderFetchSequenceRef.current + 1;
    commanderFetchSequenceRef.current = requestSequence;
    if (commanderFetchAbortControllerRef.current) {
      commanderFetchAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    commanderFetchAbortControllerRef.current = abortController;

    try {
      setIsLoadingCommanders(true);

      const selectedStatuses = Array.isArray(filters.statuses) ? filters.statuses : ['active'];
      const normalizedStatuses = selectedStatuses.map((value) => String(value || '').toLowerCase());
      let statusQuery = 'all';
      if (normalizedStatuses.includes('active') && !normalizedStatuses.includes('retired')) {
        statusQuery = 'active';
      } else if (!normalizedStatuses.includes('active') && normalizedStatuses.includes('retired')) {
        statusQuery = 'retired';
      }

      const cityLevelMin = Number.isFinite(Number(filters.cityLevelMin)) ? Number(filters.cityLevelMin) : 1;
      const cityLevelMax = Number.isFinite(Number(filters.cityLevelMax)) ? Number(filters.cityLevelMax) : 41;

      const queryParams = new URLSearchParams({
        page,
        limit: itemsPerPage,
        sortKey: sortKey || 'battlePower',
        sortDirection: sortDirection || 'desc',
        nation: filters.nation || '',
        alliance: filters.alliance || '',
        name: filters.name || '',
        rank: Array.isArray(filters.ranks) ? filters.ranks.join(',') : '',
        cityRoles: Array.isArray(filters.cityRoles) ? filters.cityRoles.join(',') : '',
        cityLevelMin: String(Math.max(1, Math.min(cityLevelMin, cityLevelMax))),
        cityLevelMax: String(Math.min(41, Math.max(cityLevelMin, cityLevelMax))),
        status: statusQuery,
      });

      const response = await axios.get(`${apiUrl}/commanders?${queryParams.toString()}`, {
        signal: abortController.signal
      });
      if (abortController.signal.aborted || commanderFetchSequenceRef.current !== requestSequence) {
        return;
      }
      const fetchedCommanders = response.data.commanders || [];
      setCommanders(fetchedCommanders);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error) {
      const wasCanceled = abortController.signal.aborted
        || error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError';
      if (wasCanceled) {
        return;
      }
      console.error('Error fetching commanders:', error);
    } finally {
      if (commanderFetchAbortControllerRef.current === abortController) {
        commanderFetchAbortControllerRef.current = null;
      }
      if (commanderFetchSequenceRef.current === requestSequence) {
        setIsLoadingCommanders(false);
      }
    }
  }, [axios, filters, sortConfig.direction, sortConfig.key]);

  useEffect(() => {
    if (!isCommanderListView) return;

    const debounceTimer = setTimeout(() => {
      fetchCommanders(1);
    }, 350);

    return () => clearTimeout(debounceTimer);
  }, [filters, sortConfig, isCommanderListView, fetchCommanders]);

  useEffect(() => {
    if (isCommanderListView) return;
    if (commanderFetchAbortControllerRef.current) {
      commanderFetchAbortControllerRef.current.abort();
      commanderFetchAbortControllerRef.current = null;
    }
    setIsLoadingCommanders(false);
  }, [isCommanderListView]);

  useEffect(() => () => {
    if (commanderFetchAbortControllerRef.current) {
      commanderFetchAbortControllerRef.current.abort();
      commanderFetchAbortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isCommanderListView) return;
    setSelectedCommanderMap((previous) => {
      const next = { ...previous };
      let changed = false;
      commanders.forEach((commander) => {
        if (selectedCommanderIds.includes(commander._id)) {
          next[commander._id] = commander;
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [commanders, selectedCommanderIds, isCommanderListView]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileFiltersOpen(false);
    }
  }, [isMobile]);

  const displayedCommanders = useMemo(() => {
    const fetchedById = new Map(commanders.map((commander) => [commander._id, commander]));
    const allowedCityRoles = Array.isArray(filters.cityRoles) && filters.cityRoles.length
      ? new Set(filters.cityRoles)
      : null;
    const allowedStatuses = Array.isArray(filters.statuses) && filters.statuses.length
      ? new Set(filters.statuses)
      : null;

    const keepByCityRole = (commander) => {
      if (!allowedCityRoles) return true;
      const role = String(commander?.cityRole || 'primary').toLowerCase();
      return allowedCityRoles.has(role);
    };

    const keepByStatus = (commander) => {
      if (!allowedStatuses) return true;
      const normalized = String(commander?.status || 'active').toLowerCase();
      const statusValue = normalized === 'retired' ? 'retired' : 'active';
      return allowedStatuses.has(statusValue);
    };

    const keepCommander = (commander) => keepByCityRole(commander) && keepByStatus(commander);

    const pinnedOutsideFilter = selectedCommanderIds
      .filter((id) => !fetchedById.has(id))
      .map((id) => selectedCommanderMap[id])
      .filter(Boolean)
      .filter(keepCommander)
      .map((commander) => ({ ...commander, isPinnedOutsideFilter: true }));

    const filteredFetched = commanders.filter(keepCommander);

    return [...pinnedOutsideFilter, ...filteredFetched];
  }, [commanders, selectedCommanderIds, selectedCommanderMap, filters.cityRoles, filters.statuses]);

  const selectedMobileRanking = useMemo(
    () => mobileRankingOptions.find((option) => option.value === mobileRankingKey) || mobileRankingOptions[0],
    [mobileRankingKey]
  );

  const nationFilterOptions = useMemo(
    () => Array.from(new Set(commanders.map((commander) => String(commander?.nation?.number || '')).filter(Boolean))).sort((a, b) => Number(a) - Number(b)),
    [commanders]
  );

  const allianceFilterOptions = useMemo(
    () => Array.from(new Set(commanders.map((commander) => String(commander?.alliance?.acronym || commander?.alliance?.name || '')).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [commanders]
  );

  const commanderNameFilterOptions = useMemo(
    () => Array.from(new Set(commanders.map((commander) => String(commander?.name || '')).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [commanders]
  );

  const historyQueryDefaults = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const commanderIds = (params.get('commanderIds') || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return {
      commanderIds,
      nationId: params.get('nationId') || ''
    };
  }, [location.search]);

  const Pagination = () => (
    <div className="commander-pagination">
      <button disabled={currentPage === 1} onClick={() => fetchCommanders(currentPage - 1)}>
        Previous
      </button>
      <span>
        Page {currentPage} of {totalPages}
      </span>
      <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => fetchCommanders(currentPage + 1)}>
        Next
      </button>
    </div>
  );

  const handleEditClick = (commander) => {
    setSelectedCommander(commander);
    setIsEditModalOpen(true);
  };

  const handleMergeClick = (commander) => {
    setSelectedCommander(commander);
    setIsMergeModalOpen(true);
  };

  const handleMergeSuccess = () => {
    setIsMergeModalOpen(false);
    fetchCommanders(currentPage);
  };

  const handleDeleteClick = async (commander) => {
    const confirmation = window.confirm(`Are you sure you want to delete commander ${commander.name}? This will also delete the associated city tiles.`);
    if (!confirmation) return;

    try {
      const commanderNationId = getNationId(commander?.nation) || userNationId;
      if (!commanderNationId) {
        window.alert('Unable to determine commander nation for delete request.');
        return;
      }
      await axios.delete(`${apiUrl}/nations/${commanderNationId}/commanders/${commander._id}`);
      setSelectedCommanderIds((previous) => previous.filter((id) => id !== commander._id));
      setSelectedCommanderMap((previous) => {
        const next = { ...previous };
        delete next[commander._id];
        return next;
      });
      fetchCommanders(currentPage);
    } catch (error) {
      console.error('Error deleting commander or associated tiles:', error);
    }
  };

  const handleRetireClick = async (commander) => {
    const confirmation = window.confirm(`Retire commander ${commander.name}? This keeps history while removing them from active workflows.`);
    if (!confirmation) return;

    try {
      const commanderNationId = getNationId(commander?.nation) || userNationId;
      if (!commanderNationId) {
        window.alert('Unable to determine commander nation for retire request.');
        return;
      }
      await axios.patch(`${apiUrl}/nations/${commanderNationId}/commanders/${commander._id}/retire`, {
        reason: 'Retired via commander admin UI',
      });
      fetchCommanders(currentPage);
    } catch (error) {
      console.error('Error retiring commander:', error);
      window.alert(error?.response?.data?.message || 'Failed to retire commander.');
    }
  };

  const handleUnretireClick = async (commander) => {
    const confirmation = window.confirm(`Unretire commander ${commander.name}?`);
    if (!confirmation) return;

    try {
      const commanderNationId = getNationId(commander?.nation) || userNationId;
      if (!commanderNationId) {
        window.alert('Unable to determine commander nation for unretire request.');
        return;
      }
      await axios.patch(`${apiUrl}/nations/${commanderNationId}/commanders/${commander._id}/unretire`);
      fetchCommanders(currentPage);
    } catch (error) {
      console.error('Error unretiring commander:', error);
      window.alert(error?.response?.data?.message || 'Failed to unretire commander.');
    }
  };

  const handleAddCommanderClick = () => {
    setIsAddCommanderModalOpen(true);
  };

  const resetFilters = () => {
    setFilters({
      nation: '',
      alliance: '',
      name: '',
      ranks: [],
      statuses: ['active'],
      cityRoles: ['primary', 'secondary'],
      battlePower: '',
      officerPower: '',
      titanPower: '',
      warplanePower: '',
      cityLevelMin: 1,
      cityLevelMax: 41,
    });
  };

  const handleSort = (key) => {
    if (isLoadingCommanders) return;
    let direction = descendingDefaultSortKeys.has(key) ? 'desc' : 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleMobileRankingChange = (event) => {
    const nextKey = event.target.value;
    setMobileRankingKey(nextKey);
    setSortConfig((previous) => ({
      key: nextKey,
      direction: previous.key === nextKey ? previous.direction : 'desc',
    }));
  };

  const handleMobileSortDirectionToggle = () => {
    if (isLoadingCommanders) return;
    setSortConfig((previous) => ({
      key: selectedMobileRanking.value,
      direction: previous.key === selectedMobileRanking.value && previous.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const cityRoleLabel = (cityRole) => {
    const normalized = String(cityRole || 'primary').toLowerCase();
    if (normalized === 'secondary') return 'Secondary';
    if (normalized === 'farm') return 'Farm';
    return 'Main';
  };

  const statusLabel = (status) => String(status || 'active').toLowerCase() === 'retired' ? 'Retired' : 'Active';

  const handleStatusesChange = (nextStatuses) => {
    const normalized = Array.isArray(nextStatuses) ? nextStatuses : [];
    setFilters((previous) => ({ ...previous, statuses: normalized }));
  };

  const handleCityRolesChange = (nextRoles) => {
    const normalized = Array.isArray(nextRoles) ? nextRoles : [];
    setFilters((previous) => ({ ...previous, cityRoles: normalized }));
  };

  const handleRanksChange = (nextRanks) => {
    const normalized = Array.isArray(nextRanks) ? nextRanks : [];
    setFilters((previous) => ({ ...previous, ranks: normalized }));
  };

  const renderMultiFilterSelect = ({ label, value, onChange, options, emptyLabel = 'None' }) => (
    <FormControl size="small" className="commanders-multi-filter" sx={{ minWidth: 170 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(event) => onChange(event.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          const selectedValues = Array.isArray(selected) ? selected : [];
          const labels = options
            .filter((option) => selectedValues.includes(option.value))
            .map((option) => option.label);
          return labels.length ? labels.join(', ') : emptyLabel;
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const setTextFilterValue = (name, value) => {
    setFilters((previous) => ({
      ...previous,
      [name]: String(value || ''),
    }));
  };

  const setCityLevelBound = (bound, rawValue) => {
    const digits = String(rawValue || '').replace(/[^\d]/g, '');
    if (digits === '') {
      setFilters((previous) => ({
        ...previous,
        [bound]: '',
      }));
      return;
    }
    const parsed = Number(digits);
    const clamped = Math.max(1, Math.min(41, parsed));

    setFilters((previous) => {
      const min = Number(previous.cityLevelMin || 1);
      const max = Number(previous.cityLevelMax || 41);
      if (bound === 'cityLevelMin') {
        return {
          ...previous,
          cityLevelMin: Math.min(clamped, Number.isFinite(max) ? max : 41),
        };
      }
      return {
        ...previous,
        cityLevelMax: Math.max(clamped, Number.isFinite(min) ? min : 1),
      };
    });
  };

  const handleCityLevelSliderChange = (_, newValue) => {
    if (!Array.isArray(newValue) || newValue.length !== 2) return;
    setFilters((previous) => ({
      ...previous,
      cityLevelMin: Number(newValue[0]),
      cityLevelMax: Number(newValue[1]),
    }));
  };

  const renderSearchFilterDropdown = ({ label, value, onChange, options, placeholder }) => (
    <Autocomplete
      freeSolo
      options={options}
      inputValue={value || ''}
      onInputChange={(_, nextInput) => onChange(nextInput)}
      onChange={(_, selected) => onChange(typeof selected === 'string' ? selected : (selected || ''))}
      className="commanders-search-filter"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
        />
      )}
    />
  );

  const renderCityLevelRangeControl = () => (
    <div className="commanders-city-level-range">
      <span className="commanders-city-level-label">City Level</span>
      <div className="commanders-city-level-inputs">
        <TextField
          size="small"
          value={filters.cityLevelMin}
          onChange={(event) => setCityLevelBound('cityLevelMin', event.target.value)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
        <Slider
          value={[
            Number.isFinite(Number(filters.cityLevelMin)) ? Number(filters.cityLevelMin) : 1,
            Number.isFinite(Number(filters.cityLevelMax)) ? Number(filters.cityLevelMax) : 41,
          ]}
          min={1}
          max={41}
          onChange={handleCityLevelSliderChange}
          valueLabelDisplay="auto"
          size="small"
        />
        <TextField
          size="small"
          value={filters.cityLevelMax}
          onChange={(event) => setCityLevelBound('cityLevelMax', event.target.value)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </div>
    </div>
  );

  const isUserNation = useCallback((commander) => {
    if (user?.role === 'superUser') return true;
    const commanderNationId = getNationId(commander?.nation);
    if (commanderNationId && userNationId && commanderNationId === userNationId) {
      return true;
    }

    const commanderNationNumber = getNationNumber(commander?.nation);
    return Boolean(
      commanderNationNumber &&
      userNationNumber &&
      commanderNationNumber === userNationNumber
    );
  }, [getNationId, getNationNumber, user?.role, userNationId, userNationNumber]);

  const toggleCommanderSelection = (commander) => {
    if (!canCommanderBeSelected(commander)) return;
    const commanderId = commander._id;

    setSelectedCommanderIds((previous) => {
      if (previous.includes(commanderId)) {
        return previous.filter((id) => id !== commanderId);
      }
      return [...previous, commanderId];
    });

    setSelectedCommanderMap((previous) => {
      const next = { ...previous };
      if (next[commanderId]) {
        delete next[commanderId];
      } else {
        next[commanderId] = commander;
      }
      return next;
    });
  };

  const clearSelectedCommanders = () => {
    setSelectedCommanderIds([]);
    setSelectedCommanderMap({});
  };

  const navigateToHistoryForCommander = (commander) => {
    const params = new URLSearchParams();
    params.set('commanderIds', commander._id);
    const commanderNationId = getNationId(commander?.nation);
    if (commanderNationId) {
      params.set('nationId', commanderNationId);
    }
    navigate(`/commanders/history?${params.toString()}`);
  };

  const handleBulkActionApply = () => {
    if (selectedCommanderIds.length === 0) {
      window.alert('Select at least one commander first.');
      return;
    }

    if (bulkAction === 'viewHistory') {
      const params = new URLSearchParams();
      params.set('commanderIds', selectedCommanderIds.join(','));

      const selectedNations = new Set(
        selectedCommanderIds
          .map((id) => getNationId(selectedCommanderMap[id]?.nation))
          .filter(Boolean)
          .map((id) => String(id))
      );

      if (selectedNations.size === 1) {
        params.set('nationId', [...selectedNations][0]);
      }

      navigate(`/commanders/history?${params.toString()}`);
      return;
    }

    if (bulkAction === 'deleteSelected') {
      if (!canDeleteCommanders) {
        window.alert('You are not authorized to delete commanders.');
        return;
      }

      const selectedCommanders = selectedCommanderIds
        .map((id) => selectedCommanderMap[id])
        .filter(Boolean)
        .filter((commander) => isUserNation(commander));

      if (selectedCommanders.length === 0) {
        window.alert('No eligible selected commanders found for deletion.');
        return;
      }

      const notEligibleCount = selectedCommanderIds.length - selectedCommanders.length;
      const confirmationMessage = `Delete ${selectedCommanders.length} selected commander(s)? This will also delete associated city tiles.` +
        (notEligibleCount > 0 ? `\n\n${notEligibleCount} selected commander(s) are outside your allowed nation scope and will be skipped.` : '');
      const confirmed = window.confirm(confirmationMessage);
      if (!confirmed) return;

      const commanderDeletes = selectedCommanders.map((commander) => {
        const commanderNationId = getNationId(commander?.nation) || userNationId;
        if (!commanderNationId) {
          return Promise.resolve({
            ok: false,
            id: commander._id,
            error: new Error('Unable to determine commander nation id')
          });
        }
        return axios
          .delete(`${apiUrl}/nations/${commanderNationId}/commanders/${commander._id}`)
          .then(() => ({ ok: true, id: commander._id }))
          .catch((error) => ({ ok: false, id: commander._id, error }));
      });

      Promise.all(commanderDeletes)
        .then((results) => {
          const deletedIds = results.filter((result) => result.ok).map((result) => result.id);
          const failedCount = results.length - deletedIds.length;

          if (deletedIds.length > 0) {
            setSelectedCommanderIds((previous) => previous.filter((id) => !deletedIds.includes(id)));
            setSelectedCommanderMap((previous) => {
              const next = { ...previous };
              deletedIds.forEach((id) => delete next[id]);
              return next;
            });
            fetchCommanders(currentPage);
          }

          if (failedCount > 0) {
            window.alert(`Deleted ${deletedIds.length} commander(s). Failed to delete ${failedCount} commander(s).`);
          } else {
            window.alert(`Deleted ${deletedIds.length} commander(s).`);
          }
        })
        .catch((error) => {
          console.error('Error executing bulk delete:', error);
          window.alert('Bulk delete failed. Please try again.');
        });
    }
  };

  const canCommanderBeSelected = useCallback((commander) =>
    isUserNation(commander) && (canReadHistory || canWriteCommanders || canDeleteCommanders),
  [isUserNation, canReadHistory, canWriteCommanders, canDeleteCommanders]);

  const selectableFilteredCommanders = useMemo(
    () => displayedCommanders.filter((commander) => canCommanderBeSelected(commander)),
    [displayedCommanders, canCommanderBeSelected]
  );

  const allFilteredSelected =
    selectableFilteredCommanders.length > 0 &&
    selectableFilteredCommanders.every((commander) => selectedCommanderIds.includes(commander._id));

  const toggleSelectAllFiltered = () => {
    if (selectableFilteredCommanders.length === 0) return;

    const filteredIds = selectableFilteredCommanders.map((commander) => commander._id);
    if (allFilteredSelected) {
      setSelectedCommanderIds((previous) => previous.filter((id) => !filteredIds.includes(id)));
      setSelectedCommanderMap((previous) => {
        const next = { ...previous };
        filteredIds.forEach((id) => delete next[id]);
        return next;
      });
      return;
    }

    setSelectedCommanderIds((previous) => {
      const nextSet = new Set(previous);
      filteredIds.forEach((id) => nextSet.add(id));
      return Array.from(nextSet);
    });
    setSelectedCommanderMap((previous) => {
      const next = { ...previous };
      selectableFilteredCommanders.forEach((commander) => {
        next[commander._id] = commander;
      });
      return next;
    });
  };

  const renderCommanderActions = (commander, mode = 'table') => (
    <div className={mode === 'mobile' ? 'commander-card-actions' : undefined}>
      {canReadHistory && (
        <button
          type="button"
          className={mode === 'mobile' ? 'commander-card-action-button' : 'commander-icon-button'}
          onClick={() => navigateToHistoryForCommander(commander)}
          title="View commander history"
        >
          <FaHistory className="history-icon" />
          {mode === 'mobile' && <span>History</span>}
        </button>
      )}
      {canWriteCommanders && isUserNation(commander) && (
        <>
          <button
            type="button"
            className={mode === 'mobile' ? 'commander-card-action-button' : 'commander-icon-button'}
            onClick={() => handleEditClick(commander)}
            title="Edit commander"
          >
            <FaEdit className="edit-icon" />
            {mode === 'mobile' && <span>Edit</span>}
          </button>
          {mode !== 'mobile' && (
            <button
              type="button"
              className="commander-icon-button"
              onClick={() => handleMergeClick(commander)}
              title="Merge commander"
            >
              <MdCallMerge className="merge-icon" />
            </button>
          )}
        </>
      )}
      {canWriteCommanders && isUserNation(commander) && String(commander?.status || 'active') !== 'retired' && (
        <button
          type="button"
          className={mode === 'mobile' ? 'commander-card-action-button' : 'commander-icon-button'}
          onClick={() => handleRetireClick(commander)}
          title="Retire commander"
        >
          <FaArchive className="merge-icon" />
          {mode === 'mobile' && <span>Retire</span>}
        </button>
      )}
      {canWriteCommanders && isUserNation(commander) && String(commander?.status || 'active') === 'retired' && (
        <button type="button" className={mode === 'mobile' ? 'commander-card-action-button' : 'small-btn'} onClick={() => handleUnretireClick(commander)}>
          Unretire
        </button>
      )}
      {canDeleteCommanders && isUserNation(commander) && (
        <button
          type="button"
          className={mode === 'mobile' ? 'commander-card-action-button danger' : 'commander-icon-button'}
          onClick={() => handleDeleteClick(commander)}
          title="Delete commander"
        >
          <FaTrashAlt className="delete-icon" />
          {mode === 'mobile' && <span>Delete</span>}
        </button>
      )}
    </div>
  );

  const tabValue = isHistoryView
    ? 'history'
    : isReviewIngestionView
      ? 'review-ingestion'
      : isOrphansView
        ? 'orphans'
        : 'list';

  const commanderTabs = [
    { value: 'list', label: 'Commander List' },
    ...(canReadHistory ? [{ value: 'history', label: 'Commander History' }] : []),
    ...(user?.role === 'superUser' ? [{ value: 'review-ingestion', label: 'Review Ingestion' }] : []),
    ...(user?.role === 'superUser' ? [{ value: 'orphans', label: 'Orphan Admin' }] : []),
  ];

  const handleTabChange = (next) => {
    if (next === 'list') navigate('/commanders');
    if (next === 'history') navigate('/commanders/history');
    if (next === 'review-ingestion') navigate('/commanders/review-ingestion');
    if (next === 'orphans') navigate('/commanders/orphans');
  };

  return (
    <PageShell
      maxWidth={false}
      containerSx={{ px: { xs: 2, md: 3 }, maxWidth: "none" }}
    >
      <div className="commanders-page">
      <div className="commanders-submenu-row">
        <AdminTabs
          tabs={commanderTabs}
          value={tabValue}
          onChange={handleTabChange}
          className="commanders-view-tabs"
        />
        {isCommanderListView && canWriteCommanders && (
          <div className="commanders-submenu-actions">
            <AppButton
              className="commanders-primary-button compact-button"
              onClick={handleAddCommanderClick}
              disabled={isLoadingCommanders}
            >
              Add Commander
            </AppButton>
          </div>
        )}
      </div>

      {isCommanderListView ? (
        <DataTableShell>
          <FilterRow className="commanders-toolbar">
            <div className="selection-summary">
              <span className="selection-count">Selected: {selectedCommanderIds.length}</span>
            </div>
            <div className="commanders-list-actions">
              {isMobile ? (
                <AppButton
                  kind="secondary"
                  className="commanders-secondary-button compact-button"
                  onClick={() => setIsMobileFiltersOpen((prev) => !prev)}
                  disabled={isLoadingCommanders}
                >
                  {isMobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
                </AppButton>
              ) : (
                <>
                  {renderMultiFilterSelect({
                    label: 'Status',
                    value: filters.statuses || [],
                    onChange: handleStatusesChange,
                    options: statusFilterOptions,
                  })}
                  {renderMultiFilterSelect({
                    label: 'City Type',
                    value: filters.cityRoles || [],
                    onChange: handleCityRolesChange,
                    options: cityTypeFilterOptions,
                  })}
                  <AppButton kind="secondary" className="commanders-secondary-button compact-button" onClick={resetFilters} disabled={isLoadingCommanders}>
                    Reset Filters
                  </AppButton>
                </>
              )}
            </div>

            <div className="bulk-actions">
              <button onClick={clearSelectedCommanders} disabled={selectedCommanderIds.length === 0 || isLoadingCommanders}>
                Clear Selected
              </button>
              {canReadHistory && (
                <>
                  <select value={bulkAction} onChange={(event) => setBulkAction(event.target.value)} disabled={isLoadingCommanders}>
                    <option value="viewHistory">View History</option>
                    {canDeleteCommanders && (
                      <option value="deleteSelected">Delete Selected</option>
                    )}
                  </select>
                  <button onClick={handleBulkActionApply} disabled={selectedCommanderIds.length === 0 || isLoadingCommanders}>
                    Apply Bulk Action
                  </button>
                </>
              )}
            </div>

            {!isMobile && (
              <div className="commanders-toolbar-filters">
                {renderSearchFilterDropdown({
                  label: 'Nation',
                  value: filters.nation,
                  onChange: (next) => setTextFilterValue('nation', next),
                  options: nationFilterOptions,
                  placeholder: 'Search nation',
                })}
                {renderSearchFilterDropdown({
                  label: 'Alliance',
                  value: filters.alliance,
                  onChange: (next) => setTextFilterValue('alliance', next),
                  options: allianceFilterOptions,
                  placeholder: 'Search alliance',
                })}
                {renderSearchFilterDropdown({
                  label: 'Commander',
                  value: filters.name,
                  onChange: (next) => setTextFilterValue('name', next),
                  options: commanderNameFilterOptions,
                  placeholder: 'Search commander',
                })}
                {renderMultiFilterSelect({
                  label: 'Rank',
                  value: filters.ranks || [],
                  onChange: handleRanksChange,
                  options: rankFilterOptions,
                  emptyLabel: 'All ranks',
                })}
                {renderCityLevelRangeControl()}
              </div>
            )}
          </FilterRow>

          {isMobile && isMobileFiltersOpen && (
            <div className="commanders-mobile-filters" aria-label="Commander filters">
              <div className="commanders-mobile-filters-grid">
                {renderSearchFilterDropdown({
                  label: 'Commander',
                  value: filters.name,
                  onChange: (next) => setTextFilterValue('name', next),
                  options: commanderNameFilterOptions,
                  placeholder: 'Search commander',
                })}
                {renderSearchFilterDropdown({
                  label: 'Nation',
                  value: filters.nation,
                  onChange: (next) => setTextFilterValue('nation', next),
                  options: nationFilterOptions,
                  placeholder: 'Search nation',
                })}
                {renderSearchFilterDropdown({
                  label: 'Alliance',
                  value: filters.alliance,
                  onChange: (next) => setTextFilterValue('alliance', next),
                  options: allianceFilterOptions,
                  placeholder: 'Search alliance',
                })}
                {renderMultiFilterSelect({
                  label: 'Rank',
                  value: filters.ranks || [],
                  onChange: handleRanksChange,
                  options: rankFilterOptions,
                  emptyLabel: 'All ranks',
                })}
              </div>
              {renderCityLevelRangeControl()}
              <div className="commanders-mobile-multi-filters" aria-label="Status and city type filters">
                {renderMultiFilterSelect({
                  label: 'Status',
                  value: filters.statuses || [],
                  onChange: handleStatusesChange,
                  options: statusFilterOptions,
                })}
                {renderMultiFilterSelect({
                  label: 'City Type',
                  value: filters.cityRoles || [],
                  onChange: handleCityRolesChange,
                  options: cityTypeFilterOptions,
                })}
              </div>
              <div className="commanders-mobile-filters-actions">
                <AppButton kind="secondary" onClick={resetFilters} disabled={isLoadingCommanders}>
                  Reset Filters
                </AppButton>
                <AppButton kind="secondary" onClick={() => setIsMobileFiltersOpen(false)}>
                  Close
                </AppButton>
              </div>
            </div>
          )}

          {isMobile && (
            <div className="commanders-mobile-ranking-control">
              <label htmlFor="mobile-ranking-select">Ranking</label>
              <div className="commanders-mobile-ranking-row">
                <select id="mobile-ranking-select" value={mobileRankingKey} onChange={handleMobileRankingChange} disabled={isLoadingCommanders}>
                  {mobileRankingOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={handleMobileSortDirectionToggle} disabled={isLoadingCommanders}>
                  {sortConfig.key === selectedMobileRanking.value && sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}
                </button>
              </div>
            </div>
          )}

          {isMobile ? (
            <div className="commander-card-list" aria-label="Commander rankings">
              <div className="commander-card-list-header">
                <span>{displayedCommanders.length.toLocaleString()} commanders</span>
                <button
                  type="button"
                  onClick={toggleSelectAllFiltered}
                  disabled={selectableFilteredCommanders.length === 0 || isLoadingCommanders}
                >
                  {allFilteredSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {displayedCommanders.map((commander, commanderIndex) => {
                const isSelected = selectedCommanderIds.includes(commander._id);
                const canSelectCommander = canCommanderBeSelected(commander);
                const displayRank = ((currentPage - 1) * itemsPerPage) + commanderIndex + 1;

                return (
                  <article
                    key={commander._id}
                    className={`commander-card ${isSelected ? 'selected' : ''} ${commander.isPinnedOutsideFilter ? 'pinned' : ''}`}
                  >
                    <div className="commander-card-main">
                      <div className="commander-card-title-block">
                        <div className="commander-card-title">
                          <span className="commander-card-rank">{displayRank}</span>
                          <span>#{commander.nation ? commander.nation.number : 'N/A'}</span>
                          <span>({commander.alliance ? commander.alliance.acronym : 'N/A'})</span>
                          <span>{commander.name || 'N/A'}</span>
                          <span>{commander.rank || 'N/A'}</span>
                          <strong>{formatRankingValue(selectedMobileRanking.getValue(commander))}</strong>
                          {String(commander?.status || 'active') === 'retired' && <span className="commander-card-badge muted">Retired</span>}
                          {commander.isPinnedOutsideFilter && <span className="commander-card-badge">Pinned</span>}
                        </div>
                        <div className="commander-card-subtitle">
                          <span>C{commander.cityLevel || 'N/A'}</span>
                          <span>{cityRoleLabel(commander?.cityRole)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="commander-card-tools">
                      {canSelectCommander && (
                        <label className="commander-card-select">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isLoadingCommanders}
                            onChange={() => toggleCommanderSelection(commander)}
                          />
                          <span>Select</span>
                        </label>
                      )}
                      {String(commander?.status || 'active') === 'retired' && <span className="commander-card-status">Retired</span>}
                    </div>

                    {renderCommanderActions(commander, 'mobile')}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="narrow-column">
                    <button
                      type="button"
                      className="commander-select-column-button"
                      onClick={toggleSelectAllFiltered}
                      disabled={selectableFilteredCommanders.length === 0 || isLoadingCommanders}
                    >
                      {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </th>
                  {!isMobile && (
                    <th
                      className={`sortable narrow-column ${sortConfig.key === 'updated' ? sortConfig.direction : ''}`}
                      onClick={() => handleSort('updated')}
                    >
                      Updated
                    </th>
                  )}
                  <th
                    className={`sortable narrow-column ${sortConfig.key === 'nation' ? sortConfig.direction : ''}`}
                    onClick={() => handleSort('nation')}
                  >
                    Nation
                  </th>
                  <th
                    className={`sortable narrow-column ${sortConfig.key === 'alliance' ? sortConfig.direction : ''}`}
                    onClick={() => handleSort('alliance')}
                  >
                    Alliance
                  </th>
                  <th className={`sortable ${sortConfig.key === 'name' ? sortConfig.direction : ''}`} onClick={() => handleSort('name')}>
                    Name
                  </th>
                  {!isMobile && <th className="narrow-column">Status</th>}
                  {!isMobile && <th className="narrow-column">City Type</th>}
                  {!isMobile && (
                    <th
                      className={`sortable narrow-column ${sortConfig.key === 'rank' ? sortConfig.direction : ''}`}
                      onClick={() => handleSort('rank')}
                    >
                      Rank
                    </th>
                  )}
                  {!isMobile && (
                    <th
                      className={`sortable narrow-column ${sortConfig.key === 'cityLevel' ? sortConfig.direction : ''}`}
                      onClick={() => handleSort('cityLevel')}
                    >
                      City Level
                    </th>
                  )}
                  <th
                    className={`sortable ${sortConfig.key === (isMobile ? selectedMobileRanking.value : 'battlePower') ? sortConfig.direction : ''}`}
                    onClick={() => handleSort(isMobile ? selectedMobileRanking.value : 'battlePower')}
                  >
                    {isMobile ? selectedMobileRanking.label : 'Battle Power'}
                  </th>
                  {!isMobile && (
                    <>
                      <th
                        className={`sortable ${sortConfig.key === 'individualReputation' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('individualReputation')}
                      >
                        Reputation
                      </th>
                      <th className={`sortable ${sortConfig.key === 'militaryRank' ? sortConfig.direction : ''}`} onClick={() => handleSort('militaryRank')}>
                        Military Rank
                      </th>
                      <th className={`sortable ${sortConfig.key === 'totalMerit' ? sortConfig.direction : ''}`} onClick={() => handleSort('totalMerit')}>
                        Total Merit
                      </th>
                      <th className={`sortable ${sortConfig.key === 'kills' ? sortConfig.direction : ''}`} onClick={() => handleSort('kills')}>
                        Kills
                      </th>
                      <th className={`sortable ${sortConfig.key === 'losses' ? sortConfig.direction : ''}`} onClick={() => handleSort('losses')}>
                        Losses
                      </th>
                      <th
                        className={`sortable ${sortConfig.key === 'officerPower' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('officerPower')}
                      >
                        Officer Power
                      </th>
                      <th className={`sortable ${sortConfig.key === 'titanPower' ? sortConfig.direction : ''}`} onClick={() => handleSort('titanPower')}>
                        Titan Power
                      </th>
                      <th
                        className={`sortable ${sortConfig.key === 'warplanePower' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('warplanePower')}
                      >
                        Warplane Power
                      </th>
                      <th
                        className={`sortable ${sortConfig.key === 'islandScore' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('islandScore')}
                      >
                        Island
                      </th>
                      <th
                        className={`sortable ${sortConfig.key === 'antiquesScore' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('antiquesScore')}
                      >
                        Antiques
                      </th>
                    </>
                  )}
                  <th className="narrow-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedCommanders.map((commander) => {
                  const isSelected = selectedCommanderIds.includes(commander._id);
                  const canSelectCommander = canCommanderBeSelected(commander);

                  return (
                    <tr
                      key={commander._id}
                      className={`${isSelected ? 'selected-row' : ''} ${commander.isPinnedOutsideFilter ? 'pinned-row' : ''}`}
                    >
                      <td className="narrow-column">
                        {canSelectCommander && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isLoadingCommanders}
                            onChange={() => toggleCommanderSelection(commander)}
                          />
                        )}
                      </td>
                      {!isMobile && <td>{commander.updatedAt ? new Date(commander.updatedAt).toLocaleDateString() : 'N/A'}</td>}
                      <td>{commander.nation ? commander.nation.number : 'N/A'}</td>
                      <td>{commander.alliance ? commander.alliance.acronym : 'N/A'}</td>
                      <td>
                        {commander.name}
                        {String(commander?.status || 'active') === 'retired' && <span className="pinned-label"> (Retired)</span>}
                        {commander.isPinnedOutsideFilter && <span className="pinned-label"> (Pinned)</span>}
                        {isMobile && (
                          <span className="commander-mobile-meta"> · {statusLabel(commander?.status)} · {cityRoleLabel(commander?.cityRole)}</span>
                        )}
                      </td>
                      {!isMobile && <td>{statusLabel(commander?.status)}</td>}
                      {!isMobile && <td>{cityRoleLabel(commander?.cityRole)}</td>}
                      {!isMobile && <td>{commander.rank || 'N/A'}</td>}
                      {!isMobile && <td>{commander.cityLevel || 'N/A'}</td>}
                      <td>
                        {isMobile
                          ? formatRankingValue(selectedMobileRanking.getValue(commander))
                          : formatRankingValue(commander.battlePower)}
                      </td>
                      {!isMobile && <td>{commander.rankingData?.individualReputation ? commander.rankingData.individualReputation.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.militaryRank || 'N/A'}</td>}
                      {!isMobile && <td>{commander.totalMerit ? commander.totalMerit.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.kills ? commander.rankingData.kills.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.losses ? commander.rankingData.losses.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.officer?.power ? commander.rankingData.officer.power.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.titan?.power ? commander.rankingData.titan.power.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.warplane?.power ? commander.rankingData.warplane.power.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.island?.score ? commander.rankingData.island.score.toLocaleString() : 'N/A'}</td>}
                      {!isMobile && <td>{commander.rankingData?.antiques?.score ? commander.rankingData.antiques.score.toLocaleString() : 'N/A'}</td>}
                      <td>{renderCommanderActions(commander)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
          {isLoadingCommanders && (
            <div className="commanders-loading-overlay" aria-live="polite">
              <div className="commanders-spinner" />
              <span>Loading commanders...</span>
            </div>
          )}
          <Pagination />
        </DataTableShell>
      ) : null}

      {isHistoryView ? (
        !canReadHistory ? (
          <div>You are not authorized to view commander history.</div>
        ) : (
          <CommanderHistoryView
            key={location.search}
            initialCommanderIds={historyQueryDefaults.commanderIds}
            initialNationId={historyQueryDefaults.nationId}
          />
        )
      ) : null}

      {isReviewIngestionView ? (
        user?.role === 'superUser' ? (
          <IngestionReviewView />
        ) : (
          <div>You are not authorized to review ingestion.</div>
        )
      ) : null}

      {isOrphansView ? (
        user?.role === 'superUser' ? (
          <CommanderOrphanAdminView />
        ) : (
          <div>You are not authorized to manage orphan commander references.</div>
        )
      ) : null}

      {isEditModalOpen && (
        <EditCommanderModal
          isOpen={isEditModalOpen}
          onRequestClose={() => setIsEditModalOpen(false)}
          commander={selectedCommander}
          onCommanderUpdated={() => fetchCommanders(currentPage)}
        />
      )}

      {isMergeModalOpen && (
        <MergeCommanderModal
          isOpen={isMergeModalOpen}
          selectedCommander={selectedCommander}
          onRequestClose={() => setIsMergeModalOpen(false)}
          onMergeSuccess={handleMergeSuccess}
        />
      )}

      <Modal
        isOpen={isAddCommanderModalOpen}
        onRequestClose={() => setIsAddCommanderModalOpen(false)}
        contentLabel="Add Commander"
        className="commanders-add-modal"
        overlayClassName="commanders-add-modal-overlay"
      >
        <AddCommander
          onCommanderAdded={() => {
            setIsAddCommanderModalOpen(false);
            fetchCommanders(currentPage);
          }}
        />
      </Modal>
      </div>
    </PageShell>
  );
};

export default Commanders;
